import Image from "next/image";
import Link from "next/link";
import type { NewsItem } from "@/lib/fleet-server";
import { newsTags } from "@/lib/news-utils";
import { stripHtml } from "@/lib/richtext";
import { useLocale } from "@/lib/locale-context";
import type { Locale } from "@/lib/locale-utils";

const FALLBACK = "/images/catalog-car.png";

// Текст по локали: en → *_en (фолбэк *_ru), иначе *_ru (фолбэк *_en).
function pickLoc(a?: string, b?: string, locale: Locale = "ru"): string {
  return locale === "en"
    ? (b && b.trim() ? b : a && a.trim() ? a : "")
    : (a && a.trim() ? a : b && b.trim() ? b : "");
}

// Дата публикации: ISO (YYYY-MM-DD) → RU «DD.MM.YYYY», EN «Month D, YYYY».
// Не-ISO строка возвращается как есть.
const NEWS_TAG_EN: Record<string, string> = {
  "Электро": "Electric", "Партнёрам": "Partners", "Партнерам": "Partners", "Офисы": "Offices",
  "Сервис": "Service", "Скидки": "Discounts", "Акция": "Promo", "Советы": "Tips",
  "Бизнес": "Business", "Автопарк": "Fleet", "Новинки": "New", "Сезон": "Season",
  "Акции": "Promo", "Новости": "News",
};
function tagLabel(t: string, locale: string): string { return locale === "en" ? (NEWS_TAG_EN[t] || t) : t; }

export function fmtNewsDate(d?: string, locale: Locale = "ru"): string {
  if (!d) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  if (!m) return d;
  if (locale === "en") {
    return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).toLocaleDateString("en-US", {
      day: "numeric", month: "long", year: "numeric", timeZone: "UTC",
    });
  }
  return `${m[3]}.${m[2]}.${m[1]}`;
}

export default function NewsCard({ item }: { item: NewsItem }) {
  const { locale, href } = useLocale();
  const title = pickLoc(item.title_ru, item.title_en, locale);
  const excerpt = stripHtml(pickLoc(item.excerpt_ru, item.excerpt_en, locale));
  const tags = newsTags(item, locale);
  return (
    <Link
      href={href(`/news/${item.slug}`)}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-black/[0.08] bg-[#f6faff] transition hover:shadow-[var(--shadow-card)]"
    >
      <div className="relative h-[200px] shrink-0 overflow-hidden sm:h-[220px]">
        <Image
          src={item.image || FALLBACK}
          alt={title}
          fill
          sizes="(max-width: 768px) 100vw, 384px"
          className="object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        />
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5 sm:p-6">
        {item.date ? (
          <span className="text-[14px] leading-5 text-text-muted sm:text-[15px]">{fmtNewsDate(item.date, locale)}</span>
        ) : null}
        <h3 className="line-clamp-2 text-[18px] font-semibold leading-[1.25] text-black transition group-hover:text-primary sm:text-[20px] lg:text-[22px]">
          {title}
        </h3>
        <p className="line-clamp-3 text-[13px] leading-[20px] text-text-muted sm:text-[14px]">
          {excerpt}
        </p>
        {tags.length > 0 && (
          <div className="mt-auto flex flex-wrap gap-x-2 gap-y-1 pt-1">
            {tags.slice(0, 4).map((t, i) => (
              <span key={`${t}-${i}`} className="text-[14px] font-medium leading-[1.3] text-primary">#{tagLabel(t, locale)}</span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}
